import type { ComponentType, SVGProps } from 'react';
import LegacyEmptyBarricade from '@mildang/graphics/react/legacy/empty/barricade';
import LegacyEmptyBox from '@mildang/graphics/react/legacy/empty/box';
import LegacyEmptyBrokenPencil from '@mildang/graphics/react/legacy/empty/broken-pencil';
import LegacyEmptyFallingAirplane from '@mildang/graphics/react/legacy/empty/falling-airplane';
import LegacyEmptyLock from '@mildang/graphics/react/legacy/empty/lock';
import LegacyEmptyPaper from '@mildang/graphics/react/legacy/empty/paper';
import LegacyEmptyRene from '@mildang/graphics/react/legacy/empty/rene';
import LegacyEmptySymbols from '@mildang/graphics/react/legacy/empty/symbols';
import MildangEmptyBarricade from '@mildang/graphics/react/mildang/empty/barricade';
import MildangEmptyBox from '@mildang/graphics/react/mildang/empty/box';
import MildangEmptyLock from '@mildang/graphics/react/mildang/empty/lock';
import MildangEmptyPaper from '@mildang/graphics/react/mildang/empty/paper';
import MildangEmptySymbols from '@mildang/graphics/react/mildang/empty/symbols';
import MildangEmptyUnplug from '@mildang/graphics/react/mildang/empty/unplug';

/**
 * 빈 상태 그래픽의 단일 소스. EmptyState · FallbackPage · ErrorScreen 이 공유한다.
 * 그림은 `@mildang/graphics` 의 브랜드 폴더에 있다 — mildang(밀당 서비스) · legacy(밀당 이외 사이트).
 * educore(경기도)는 상황별 그림이 없고 캐릭터 하나(`educore/character-empty`)만 쓴다.
 * 고를 수 있는 종류가 브랜드마다 달라서 타입도 브랜드별로 나눈다.
 */
type Graphic = ComponentType<SVGProps<SVGSVGElement>>;

export const MILDANG_EMPTY_GRAPHICS = {
  box: MildangEmptyBox,
  paper: MildangEmptyPaper,
  barricade: MildangEmptyBarricade,
  lock: MildangEmptyLock,
  symbols: MildangEmptySymbols,
  unplug: MildangEmptyUnplug,
} as const satisfies Record<string, Graphic>;

/** ⚠️ `rene` 는 밀당 마스코트다. */
export const LEGACY_EMPTY_GRAPHICS = {
  box: LegacyEmptyBox,
  paper: LegacyEmptyPaper,
  barricade: LegacyEmptyBarricade,
  lock: LegacyEmptyLock,
  symbols: LegacyEmptySymbols,
  rene: LegacyEmptyRene,
  brokenPencil: LegacyEmptyBrokenPencil,
  fallingAirplane: LegacyEmptyFallingAirplane,
} as const satisfies Record<string, Graphic>;

export type MildangEmptyImageType = keyof typeof MILDANG_EMPTY_GRAPHICS;
export type LegacyEmptyImageType = keyof typeof LEGACY_EMPTY_GRAPHICS;
/** 두 브랜드의 종류를 합친 것. 브랜드를 모르는 자리(문서·스토리 컨트롤)에서만 쓴다. */
export type EmptyImageType = MildangEmptyImageType | LegacyEmptyImageType;

/** 브랜드에 없는 종류(타입 밖 JS 호출 등)는 같은 브랜드의 box 로 그린다. */
export function emptyGraphicOf(brand: 'mildang' | 'legacy', type: EmptyImageType): Graphic {
  const graphics: Readonly<Partial<Record<EmptyImageType, Graphic>>> & { box: Graphic } =
    brand === 'legacy' ? LEGACY_EMPTY_GRAPHICS : MILDANG_EMPTY_GRAPHICS;
  return graphics[type] ?? graphics.box;
}
